import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';
import { useToast } from '../components/common';
import { ago } from '../nucleus/format';
import '../styles/Questions.css';

// ── Helpers ──────────────────────────────────────────────────

const Q_TYPE_META = {
  tension_resolution: { label: 'tension resolution', cls: 'q-type-tension' },
  domain_halt:        { label: 'domain · halt',      cls: 'q-type-domain'  },
  meta:               { label: 'meta · domain halt', cls: 'q-type-meta'    },
  dormant_value:      { label: 'dormant value flag', cls: 'q-type-meta'    },
  steering:           { label: 'steering',           cls: 'q-type-steering'},
  app_question:       { label: 'app question',       cls: 'q-type-steering'},
};

const STATUS_TABS = [
  { key: 'PENDING',   label: 'Pending',   filterFn: q => q.status === 'PENDING' },
  { key: 'ANSWERED',  label: 'Answered',  filterFn: q => q.status === 'ANSWERED' },
  { key: 'DEFERRED',  label: 'Deferred',  filterFn: q => q.status === 'DEFERRED' },
  { key: 'DISMISSED', label: 'Dismissed', filterFn: q => q.status === 'DISMISSED' },
];

const BADGE_CLS = {
  PENDING: 'qbadge-pending', DEFERRED: 'qbadge-deferred',
  ANSWERED: 'qbadge-answered', DISMISSED: 'qbadge-dismissed', ARCHIVED: 'qbadge-archived',
};

const PRIORITY_DOT = p => {
  if (p >= 90) return 'priority-tension';
  if (p >= 70) return 'priority-high';
  if (p >= 40) return 'priority-normal';
  return 'priority-low';
};

function QTypeChip({ type }) {
  const meta = Q_TYPE_META[type];
  if (!meta) return null;
  return <span className={`q-type-chip ${meta.cls}`}>{meta.label}</span>;
}

function QStatusBadge({ status }) {
  return <span className={`q-status-badge ${BADGE_CLS[status] || 'qbadge-pending'}`}>{status}</span>;
}

// ── Main Component ────────────────────────────────────────────

const QuestionsView = ({ panelOneMode = false }) => {
  const toast = useToast();
  const [allQuestions, setAllQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('PENDING');
  const [selected, setSelected] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [answerText, setAnswerText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getQuestions({ limit: 200 });
      setAllQuestions(data.questions || data.items || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const tabCounts = {};
  STATUS_TABS.forEach(t => { tabCounts[t.key] = allQuestions.filter(t.filterFn).length; });

  const visibleList = allQuestions
    .filter(STATUS_TABS.find(t => t.key === activeTab)?.filterFn ?? (() => true))
    .sort((a, b) => (b.priority || 50) - (a.priority || 50));

  const selectQuestion = async (q) => {
    setSelected(q);
    setAnswerText('');
    setLoadingDetail(true);
    try {
      const full = await apiService.getQuestion(q.id);
      setSelected(full);
    } catch { /* keep summary */ }
    finally { setLoadingDetail(false); }
  };

  const afterAction = async () => {
    setSubmitting(false);
    setAnswerText('');
    await fetchAll();
    if (selected) {
      try { const fresh = await apiService.getQuestion(selected.id); setSelected(fresh); }
      catch { setSelected(null); }
    }
  };

  const handleAnswer = async () => {
    if (!selected || !answerText.trim() || submitting) return;
    setSubmitting(true);
    try {
      await apiService.answerQuestion(selected.id, answerText);
      toast.success('Answer submitted');
      await afterAction();
    } catch (err) { toast.error(err.message); setSubmitting(false); }
  };

  const handleSkip = async () => {
    // Skip = dismiss without prejudice (proposals stay paused)
    if (!selected || submitting) return;
    setSubmitting(true);
    try {
      await apiService.dismissQuestion(selected.id);
      toast.success('Skipped — proposals in this domain remain paused');
      await afterAction();
    } catch (err) { toast.error(err.message); setSubmitting(false); }
  };

  const handleDefer = async () => {
    if (!selected || submitting) return;
    setSubmitting(true);
    try {
      const res = await apiService.deferQuestion(selected.id);
      toast.success(res.auto_archived ? 'Auto-archived' : `Deferred (${res.deferred_count}/3)`);
      await afterAction();
    } catch (err) { toast.error(err.message); setSubmitting(false); }
  };

  const handleDismiss = async () => {
    if (!selected || submitting) return;
    setSubmitting(true);
    try {
      await apiService.dismissQuestion(selected.id);
      toast.success('Question dismissed');
      await afterAction();
    } catch (err) { toast.error(err.message); setSubmitting(false); }
  };

  const canAct = selected && ['PENDING', 'DEFERRED'].includes(selected.status);
  const contextData = selected?.context_data || {};
  const isDomainHalt = selected?.question_type === 'domain_halt' || selected?.question_type === 'meta';

  return (
    <div className={`qq-shell${panelOneMode ? ' qq-shell--rail' : ''}`}>

      {/* ── SIDEBAR ── */}
      <div className="qq-sidebar">
        <div className="qq-sidebar-header">
          <div className="qq-sidebar-title">Questions</div>
          <div className="qq-sidebar-sub">From apps and the ontology agent</div>
        </div>

        <div className="qq-status-tabs">
          {STATUS_TABS.map(t => (
            <button
              key={t.key}
              className={`btn qq-status-tab${activeTab === t.key ? ` active${t.key === 'PENDING' ? ' warning-tab' : ''}` : ''}`}
              onClick={() => { setActiveTab(t.key); setSelected(null); }}
            >
              {t.label}
              {tabCounts[t.key] > 0 && <span className="qq-tab-count">{tabCounts[t.key]}</span>}
            </button>
          ))}
        </div>

        <div className="qq-item-list">
          {loading ? (
            <div className="qq-loading">Loading…</div>
          ) : visibleList.length === 0 ? (
            <div className="qq-empty-list">Nothing here</div>
          ) : visibleList.map(q => (
            <div
              key={q.id}
              className={`qq-list-item${selected?.id === q.id ? ' selected' : ''}`}
              onClick={() => selectQuestion(q)}
            >
              <div className="qq-item-top">
                <div className="qq-item-title-row">
                  <div className={`qq-priority-dot ${PRIORITY_DOT(q.priority || 50)}`} />
                  <div className="qq-item-title">{q.question_text}</div>
                </div>
              </div>
              <div className="qq-item-meta">
                {q.question_type && <QTypeChip type={q.question_type} />}
                <span className="qq-item-time">{ago(q.created_at)}</span>
                {q.deferred_count > 0 && (
                  <span className="qq-deferred-badge">deferred ×{q.deferred_count}</span>
                )}
              </div>
              {q.proposal_id && (
                <div className="qq-deferred-note">From {q.app_id || 'agent'} · linked to proposal</div>
              )}
              {!q.proposal_id && q.app_id && (
                <div className="qq-deferred-note">From {q.app_id}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── DETAIL PANEL ── (inline panel normally; a centered modal in rail mode) */}
      {(!panelOneMode || selected) && (
      <>
      {panelOneMode && <div className="pq-detail-backdrop" onClick={() => setSelected(null)} />}
      <div className={`qq-detail-panel${panelOneMode ? ' pq-detail-panel--modal' : ''}`}>
        {panelOneMode && selected && (
          <button type="button" className="btn pq-detail-close" onClick={() => setSelected(null)} aria-label="Close">&times;</button>
        )}
        {!selected ? (
          <div className="qq-empty-state">
            <div className="qq-empty-icon">?</div>
            <div className="qq-empty-label">Select a question to answer</div>
            <div className="qq-empty-sub">Your answers become high-authority signals</div>
          </div>
        ) : (
          <>
            {/* TOP BAR */}
            <div className="qq-detail-topbar">
              <div className="qq-detail-topbar-main">
                <div className="qq-detail-title">
                  {contextData.title || (
                    isDomainHalt ? 'Domain pause' : 'Question'
                  )}
                </div>
                <div className="qq-detail-meta-row">
                  <QStatusBadge status={selected.status} />
                  {selected.question_type && <QTypeChip type={selected.question_type} />}
                  {selected.app_id && (
                    <span className="qq-detail-source">
                      {selected.app_id} · {ago(selected.created_at)}
                    </span>
                  )}
                </div>
              </div>
              <div className="qq-action-bar">
                {canAct && (
                  <>
                    <button className="btn qq-btn qq-btn-defer" disabled={submitting} onClick={handleDefer}>Defer</button>
                    <button className="btn qq-btn qq-btn-dismiss" disabled={submitting} onClick={handleDismiss}>Dismiss</button>
                  </>
                )}
              </div>
            </div>

            {/* DETAIL BODY */}
            <div className="qq-detail-body">
              {loadingDetail && <div className="qq-loading">Loading…</div>}

              {/* Domain halt nudge card */}
              {isDomainHalt && (
                <div className="qq-nudge-card">
                  <div className="qq-nudge-label">
                    {selected.deferred_count
                      ? `${selected.deferred_count} deferrals in this domain`
                      : 'Domain pause'}
                  </div>
                  <div className="qq-nudge-text">
                    {contextData.nudge_text ||
                      'Rather than keep proposing into this gap, the agent is pausing and asking directly. Your answer will shape whether — and how — it continues proposing in this space.'}
                  </div>
                  {contextData.deferred_proposal_ids?.length > 0 && (
                    <div className="qq-nudge-actions">
                      <button className="btn qq-btn qq-btn-ghost qq-btn-sm">
                        View deferred proposals
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Deferred nudge (non-domain-halt) */}
              {!isDomainHalt && selected.status === 'DEFERRED' && selected.deferred_count >= 2 && (
                <div className="qq-nudge-card">
                  <div className="qq-nudge-label">Re-surface nudge</div>
                  <div className="qq-nudge-text">
                    You've deferred this <strong>{selected.deferred_count}×</strong>.
                    {selected.deferred_count < 3
                      ? ' One more deferral will auto-archive it.'
                      : ' This will be auto-archived on next deferral.'}
                  </div>
                </div>
              )}

              {/* Question card */}
              <div className="qq-question-card">
                <div className="qq-question-text">"{selected.question_text}"</div>

                {/* Context attached by agent */}
                {(contextData.background_text || contextData.node_refs?.length > 0) && (
                  <div className="qq-context-card">
                    <div className="qq-context-label">Context attached by agent</div>
                    {contextData.background_text && (
                      <div className="qq-context-text">{contextData.background_text}</div>
                    )}
                    {contextData.node_refs?.length > 0 && (
                      <div className="qq-node-refs">
                        {contextData.node_refs.map((ref, i) => (
                          <span key={i} className="qq-node-ref">
                            {typeof ref === 'object' ? `${ref.id} · ${ref.label}` : ref}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Domain tags */}
                {selected.domain_tags?.length > 0 && (
                  <div className="qq-domain-tags">
                    {selected.domain_tags.map((tag, i) => (
                      <span key={i} className="qq-tag-pill">{tag}</span>
                    ))}
                  </div>
                )}
              </div>

              {/* Linked proposal */}
              {selected.proposal_id && (
                <div className="qq-linked-proposal">
                  <span className="qq-linked-icon">◈</span>
                  <div>
                    <div className="qq-linked-label">Linked proposal</div>
                    <div className="qq-linked-title">{selected.proposal_id}</div>
                  </div>
                </div>
              )}

              {/* Answer area */}
              {canAct && (
                <div className="qq-answer-area">
                  <div className="qq-answer-label">Your answer</div>
                  <textarea
                    className="qq-answer-input"
                    placeholder={isDomainHalt
                      ? 'Write your answer — in your own words. The agent will use your framing, not its own, when proposing in this domain going forward.'
                      : 'Write your answer… Your response becomes a high-authority signal for the agent\'s next run.'}
                    value={answerText}
                    onChange={e => setAnswerText(e.target.value)}
                    rows={5}
                  />
                  <div className="qq-answer-hint">
                    Your answer is written as a raw signal. The agent will propose nodes from it — you'll review those in Proposals as usual.
                  </div>
                  <div className="qq-answer-bar">
                    <button
                      className="btn qq-btn qq-btn-answer"
                      disabled={submitting || !answerText.trim()}
                      onClick={handleAnswer}
                    >
                      Submit Answer
                    </button>
                    <div className="qq-answer-bar-right">
                      <button className="btn qq-btn qq-btn-ghost" disabled={submitting} onClick={handleSkip}>
                        {isDomainHalt ? 'Skip — keep proposals paused' : 'Skip'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Read-only answer */}
              {selected.status === 'ANSWERED' && selected.answer_text && (
                <div className="qq-answered-card">
                  <div className="qq-answered-label">Your answer</div>
                  <div className="qq-answered-text">{selected.answer_text}</div>
                  {selected.answered_at && (
                    <div className="qq-answered-time">Answered {ago(selected.answered_at)}</div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      </>
      )}
    </div>
  );
};

export default QuestionsView;
